import React from 'react';

type AvatarTint = 'sage' | 'blush';

interface AvatarProps {
  name?: string;
  src?: string;
  /** Emoji avatar, rendered when no photo `src` is present (photo > emoji > initials). */
  emoji?: string;
  size?: number;
  tint?: AvatarTint;
  fontSize?: number;
}

const TINT_CLASS: Record<AvatarTint, { block: string; initials: string }> = {
  sage: { block: 'bg-we-accent-2/20', initials: 'text-we-accent-2' },
  blush: { block: 'bg-we-accent/30', initials: 'text-we-clay' },
};

const deriveInitials = (name: string): string =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word[0])
    .join('')
    .toUpperCase();

/**
 * Photo, emoji, or initials utility avatar. Renders the uploaded photo when
 * `src` is present, else the `emoji` avatar when present, else initials
 * derived from `name` on a tinted, circular block (#323: photo upload is not
 * implemented, so emoji is the persisted avatar mechanism today).
 * Distinct from the messaging `we/Avatar` (#1142), which is initials-only.
 */
const Avatar: React.FC<AvatarProps> = ({
  name = '',
  src,
  emoji,
  size = 44,
  tint = 'sage',
  fontSize,
}) => {
  const tintClass = TINT_CLASS[tint];
  return (
    <span
      className={`inline-flex items-center justify-center overflow-hidden shrink-0 rounded-full border border-we-ink/15 ${tintClass.block}`}
      style={{ width: size, height: size }}
    >
      {src ? (
        <img src={src} alt={name} className="h-full w-full object-cover" />
      ) : emoji ? (
        <span aria-hidden="true" style={{ fontSize: fontSize ?? Math.round(size * 0.5) }}>
          {emoji}
        </span>
      ) : (
        <span
          className={`font-we-display font-extrabold tracking-[-0.01em] ${tintClass.initials}`}
          style={{ fontSize: fontSize ?? Math.round(size * 0.36) }}
        >
          {deriveInitials(name)}
        </span>
      )}
    </span>
  );
};

export default Avatar;
